'use client';

import { useEffect, useState } from 'react';
import { styled } from '@linaria/react';
import FollowButton from '@fe-news/components/atoms/Member/FollowButton';
import HotRate from '@fe-news/components/atoms/Member/HotRate';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import Link from 'next/link';
import { TagsGroup } from '@fe-common-ui/Group';
import useLoginModal from '@fe-news/hooks/use-login-modal';
import useMyCollection from '@fe-news/hooks/use-my-collection';
import { fetchPopTopic } from '@fe-news/actions/pop-topic';
import { useQuery } from '@tanstack/react-query';
import useUpdateMyCollection from '@fe-news/hooks/use-update-my-collection';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import Modal from '@fe-news/components/molecules/Modal';
import SuccessFollowPopUp from '@fe-news/components/molecules/SuccessFollowPopUp';

// types
import { MyCollectionProductType, HotPopTopicCardProps, LIMIT_POP_TOPIC_COUNT } from '@fe-news/constants/my-track/type';

// utils
import { isBookmark, handleFollow } from '@fe-news/utils/member';
import { postTimeDisplayFactory } from '@fe-common-utils/libs/time';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px 16px 16px 16px;
  border-radius: 6px;
  background-color: #f6f8fc;
  position: relative;
`;

const Time = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #848d97;
  margin-bottom: 16px;
`;

const Divide = styled.div`
  width: 2px;
  height: 16px;
  background-color: #e2e8f1;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #848d97;

  span {
    font-size: 24px;
  }
`;

const Title = styled.h3`
  flex: 1;
  margin-left: 4px;
  margin-right: 5px;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    -webkit-line-clamp: 2;
  }

  a {
    color: #383838;
    text-decoration: none;
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.5px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Summary = styled.div`
  margin-top: 16px;
  font-size: 14px;
  line-height: 1.71;
  text-align: justify;
  opacity: 0.9;
  color: #383838;
  font-weight: 400;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagWrapper = styled.div`
  margin-top: 10px;
  font-weight: 400;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 398px;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    min-width: 327px;
  }

  h3 {
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-align: center;
  }

  p {
    margin-top: 16px;
    font-size: 16px;
    letter-spacing: 0.5px;
    color: #383838;
    text-align: center;
  }

  button {
    margin-top: 24px;
    font-size: 14px;
    background-color: #e03f19;
    color: #fff;
    padding: 9px 14px;
    border-radius: 4px;
    cursor: pointer;
    align-self: center;
  }
`;

const HotPopTopicCard = ({ hotRank, id }: HotPopTopicCardProps) => {
  const [isClose, setIsClose] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { isLoggedIn, onClickLoginModalOpen } = useLoginModal({
    defaultCloseLoginModal: false
  });

  const {
    refetch: refetchMyCollection,
    myCollectionDataItemsIdArray,
    isLoading
  } = useMyCollection({
    productType: MyCollectionProductType.POPTOPIC,
    isLogin: isLoggedIn,
    isRankingList: true
  });

  const {
    postMyCollection,
    deleteMyCollection,
    isLoading: isUpdateMyCollectionLoading
  } = useUpdateMyCollection({
    refetchMyCollection
  });

  const { data } = useQuery({
    queryKey: ['my-pop-topic', `id:${id}`],
    queryFn: () => fetchPopTopic({ id: Number(id) })
  });

  const tags = new Set(
    [...(data?.data?.popularTopic?.tagOr ?? []), ...(data?.data?.popularTopic?.tagAnd ?? [])].filter(tag => tag !== '')
  );
  const content = data?.data?.popularTopic.content ?? '';
  const title = data?.data?.popularTopic.title ?? '';
  const time = new Date(data?.data?.popularTopic.onlineAt ?? Date.now()).getTime();
  const formatTime = postTimeDisplayFactory(time).display ?? '--';
  const isBookmarked = isBookmark(myCollectionDataItemsIdArray, id.toString());

  const handleDelete = async () => {
    const response = await deleteMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const handlePost = async () => {
    const response = await postMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const onClickFollow = () => {
    handleFollow({
      isLoggedIn,
      isBookmarked,
      newsId: id.toString(),
      dataLength: myCollectionDataItemsIdArray?.length,
      limit: LIMIT_POP_TOPIC_COUNT,
      productType: MyCollectionProductType.POPTOPIC,
      setIsSaved,
      setIsClose,
      setIsSuccess,
      handleDelete,
      handlePost,
      onClickLoginModalOpen,
      gtmEvent: {
        [GTMEventKey.EVENT_NAME]: CustomEventName.FOLLOW_POP,
        [GTMEventKey.SECTION]: 'News_Category_我的追蹤',
        [GTMEventKey.CLICK_ITEM]: `追蹤的時事＿熱門排行＿${title}`
      }
    });
  };

  const onClickTitle = () => {
    const gtmEvent = {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_POP,
      [GTMEventKey.SECTION]: 'News_Category_我的追蹤',
      [GTMEventKey.CLICK_ITEM]: `追蹤的時事＿熱門排行＿${title}`
    };
    sendGTM(gtmEvent);
  };

  const onClose = () => {
    setIsClose(true);
  };

  useEffect(() => {
    setIsSaved(isBookmarked);
  }, [isBookmarked]);

  if (title === '404' || title === '') return null;

  return (
    <Container>
      {isSuccess && <SuccessFollowPopUp setIsSuccess={setIsSuccess} />}
      <Modal isClose={isClose} onClose={onClose}>
        <Content>
          <h3>提醒您</h3>
          <p>熱門時事追蹤數量已達上限 10 則</p>
          <button onClick={onClose}>返回</button>
        </Content>
      </Modal>
      <Time>
        <span>{formatTime}</span>
        <Divide />
        <HotRate hotRank={hotRank} />
      </Time>
      <TitleContainer>
        <span role='img' aria-label='fire-emoji'>
          🔥
        </span>
        <Title>
          <Link href={`/poptopic/${id}`} onClick={onClickTitle} prefetch={false}>
            {title}
          </Link>
        </Title>

        <FollowButton
          onClickTrack={onClickFollow}
          isTracked={isSaved}
          isLoading={isUpdateMyCollectionLoading || isLoading}
        />
      </TitleContainer>
      <Summary
        dangerouslySetInnerHTML={{
          __html: content
        }}
      />
      <TagWrapper>
        <TagsGroup tags={[...tags]} backgroundColor='rgba(255, 255, 255, 0.8)' fontColor='#515c69' />
      </TagWrapper>
    </Container>
  );
};

export default HotPopTopicCard;
